import { useState } from 'react';
import { useApiClient } from '../../../api/client';
import { useToast } from '../../../context/ToastContext';
import { safeJson, fmtDate } from '../../../utils';
import type { ChangeRequest, AffectedStakeholder } from '../../../types';

interface Props { record: ChangeRequest; cid: string; onRefresh: () => Promise<void>; }

export default function NotifySection({ record, cid, onRefresh }: Props) {
  const api = useApiClient();
  const { showToast } = useToast();
  const [sending, setSending] = useState(false);
  const stakeholders = safeJson<AffectedStakeholder[]>(record.affectedStakeholders, []);

  async function reNotify() {
    setSending(true);
    try {
      await api.patch(`/changes/${encodeURIComponent(cid)}/resubmit-stakeholder-review`, {});
      showToast('Stakeholder notifications sent. Awaiting responses.', 'success');
      await onRefresh();
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="section-panel">
      <div className="section-panel__header">
        <h2 className="section-panel__title">
          <i className="fa-solid fa-bell" aria-hidden="true" /> Step 2 — Stakeholder Notifications
        </h2>
      </div>
      <div className="section-panel__body">
        <div className="alert alert--warn" style={{ marginBottom: 20 }}>
          <span className="alert__icon"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /></span>
          <div className="alert__body">
            This change has been amended following a rejection. Re-send notifications to all stakeholders so they can review the updated request and confirm whether they are affected.
          </div>
        </div>

        <div className="field-display-grid" style={{ marginBottom: 24 }}>
          <div className="field-display"><div className="field-display__label">Change ID</div><div className="field-display__value change-id-mono">{record.changeId}</div></div>
          <div className="field-display"><div className="field-display__label">Change Name</div><div className="field-display__value">{record.changeName}</div></div>
          <div className="field-display"><div className="field-display__label">Scope</div><div className="field-display__value">{record.scope}</div></div>
          <div className="field-display"><div className="field-display__label">Proposed Go-Live</div><div className="field-display__value">{fmtDate(record.proposedGolive)}</div></div>
        </div>

        <h3 style={{ marginBottom: 12 }}>Stakeholders to Notify</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
          {stakeholders.length === 0
            ? <p className="text-muted">No stakeholders on this record.</p>
            : stakeholders.map(s => (
              <div key={s.email} className="stakeholder-row">
                <div className="stakeholder-row__avatar">{s.name.charAt(0).toUpperCase()}</div>
                <div className="stakeholder-row__info">
                  <div className="stakeholder-row__name">{s.name}</div>
                  <div className="stakeholder-row__meta">{s.email}{s.department ? ` · ${s.department}` : ''}</div>
                </div>
              </div>
            ))
          }
        </div>

        <button
          className={`btn btn--primary${sending ? ' is-loading' : ''}`}
          onClick={reNotify}
          disabled={sending || stakeholders.length === 0}
        >
          <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Re-Notify Stakeholders &amp; Continue
        </button>
      </div>
    </div>
  );
}
